import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { CompanyListComponent } from "app/components/company-list/company-list.component";
import { ProjectListComponent } from "app/components/project-list/project-list.component";
import { CompanyModel } from "app/models/company.model";
import { ProjectModel } from "app/models/project.model";
import { UserModel } from "app/models/user.model";
import { AuthService } from "app/services/api/auth.service";
import { UserService } from "app/services/api/user.service";
import { AuthenticationService } from "app/services/authentication.service";
import { ToastrService } from "ngx-toastr";
import { timer } from "rxjs";
import { debounce } from "rxjs/operators";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";

@Component({
    selector: "app-user",
    templateUrl: "./user.component.html",
    styleUrls: ["./user.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class UserComponent implements OnInit {
    @ViewChild(ProjectListComponent)
    private projectList: ProjectListComponent;
    @ViewChild(CompanyListComponent)
    private companyList: CompanyListComponent;

    private user: UserModel;
    public userForm: UntypedFormGroup;
    public resetPasswordForm: UntypedFormGroup;
    pageType: string;
    savedChangesString: string;
    passwordPolicyHint: string;
    resetPasswordSuccess: string;
    minLength = 6;

    constructor(
        private translation: FuseTranslationLoaderService,
        private authService: AuthService,
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private toastr: ToastrService,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.pageType = "new";

        translation.translateService.get("GLOBAL", { value: "SavedChanges" }).subscribe((res) => {
            this.savedChangesString = res.SavedChanges;
        });
        translation.translateService.get("USERS.PasswordPolicy", { minLength: this.minLength }).subscribe((value) => {
            this.passwordPolicyHint = value;
        });
        translation.translateService.get("USERS.PasswordResetSuccess").subscribe((value) => {
            this.resetPasswordSuccess = value;
        });
    }

    ngOnInit() {
        this.user = this.route.snapshot.data.data;
        if (this.user.id) {
            this.pageType = "edit";
        }

        this.userForm = this.formBuilder.group({
            id: [this.user.id],
            name: [this.user.name, [Validators.required]],
            email: [this.user.email, [Validators.required]],
            password: ["", this.pageType === "new" ? [Validators.required, Validators.minLength(this.minLength)] : []],
            isAdmin: [this.user.isAdmin],
        });
        this.resetPasswordForm = this.formBuilder.group({
            password: ["", [Validators.required, Validators.minLength(this.minLength)]],
            confirmPassword: ["", [Validators.required]],
        });

        this.authService.getPasswordPolicy().subscribe({
            next: (policy) => {
                this.minLength = policy.minLength || this.minLength;
                this.userForm
                    .get("password")
                    .setValidators(this.pageType === "new" ? [Validators.required, Validators.minLength(this.minLength)] : []);
                this.userForm.get("password").updateValueAndValidity({ emitEvent: false });
                this.resetPasswordForm.get("password").setValidators([Validators.required, Validators.minLength(this.minLength)]);
                this.resetPasswordForm.get("password").updateValueAndValidity({ emitEvent: false });
                this.translation.translateService.get("USERS.PasswordPolicy", { minLength: this.minLength }).subscribe((value) => {
                    this.passwordPolicyHint = value;
                });
            },
        });

        if (this.pageType === "edit") {
            this.userForm.valueChanges.pipe(debounce(() => timer(1000))).subscribe((_val) => {
                this.save();
            });
        }
    }

    ngAfterViewInit() {
        if (this.pageType === "edit") {
            this.projectList.selectedProjects = this.user.projects;
            this.companyList.selectedCompanies = this.user.companies;
        }
    }

    add() {
        this.userService.create(this.userForm.value).subscribe(
            () => {
                this.router.navigate(["/admin/users"]);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    save() {
        this.userService.edit(this.userForm.value).subscribe(
            () => {
                if (this.pageType === "edit") {
                    this.toastr.success(this.savedChangesString);
                } else {
                    this.router.navigate(["/admin/users"]);
                }
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                // In case of resource removed, return to the list
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/admin/users"]);
                }
            },
        );
    }

    cancel() {
        this.router.navigate(["/admin/users"]);
    }

    resetPassword() {
        if (this.resetPasswordForm.invalid) {
            this.resetPasswordForm.markAllAsTouched();
            return;
        }

        if (this.resetPasswordForm.value.password !== this.resetPasswordForm.value.confirmPassword) {
            this.toastr.error(this.translation.translateService.instant("USERS.PasswordsDoNotMatch"));
            return;
        }

        this.authService.resetUserPassword(this.user.id, { password: this.resetPasswordForm.value.password }).subscribe(
            () => {
                this.toastr.success(this.resetPasswordSuccess);
                this.resetPasswordForm.reset();
                if (this.authenticationService.currentUser?.id === this.user.id) {
                    this.authenticationService.logout();
                    this.router.navigate(["/login"]);
                }
            },
            (error) => {
                const message = error?.error?.message || error?.message || "Error";
                this.toastr.error("Error: " + message);
            },
        );
    }

    onProjectCheck(_project: ProjectModel) {
        const selectedProjects = this.projectList.checkedProjects();
        const projectsId = selectedProjects.map((project) => project.id);

        const userEdit = new UserModel();
        userEdit.id = this.user.id;
        userEdit.projects = projectsId;

        this.userService.edit(userEdit).subscribe(
            (data) => {
                this.user = data;
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    onCompanyCheck(_company: CompanyModel) {
        const selectedCompanies = this.companyList.checkedCompanies();
        const companiesId = selectedCompanies.map((company) => company.id);

        const userEdit = new UserModel();
        userEdit.id = this.user.id;
        userEdit.companies = companiesId;

        this.userService.edit(userEdit).subscribe(
            (data) => {
                this.user = data;
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }
}
